import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Upload, Download, Loader2 } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { ACCEPT, uploadDocument, downloadUploadedDocument } from './UploadedDocuments';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atff_token')}` });

const ChecklistItem = ({ item, busy, onUpload, onDownload }) => (
  <div className="flex items-center justify-between gap-3 p-3 border rounded-lg bg-white" data-testid={`checklist-item-${item.doc_type}`}>
    <div className="flex items-start gap-3 min-w-0">
      {item.uploaded ? (
        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-testid={`checklist-status-uploaded-${item.doc_type}`} />
      ) : (
        <Circle className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" data-testid={`checklist-status-missing-${item.doc_type}`} />
      )}
      <div className="min-w-0">
        <p className="font-medium text-sm">{item.label}</p>
        <p className="text-xs text-gray-500 truncate">
          {item.uploaded ? item.document.original_filename : item.reason}
        </p>
      </div>
    </div>
    <div className="flex gap-2 shrink-0">
      {item.uploaded && (
        <Button size="sm" variant="outline" onClick={() => onDownload(item.document)} data-testid={`checklist-download-${item.doc_type}`}>
          <Download className="w-4 h-4" />
        </Button>
      )}
      <Button
        size="sm"
        variant={item.uploaded ? 'outline' : 'default'}
        className={item.uploaded ? '' : 'bg-continental hover:bg-continental/90'}
        onClick={() => onUpload(item.doc_type)}
        disabled={busy === item.doc_type}
        data-testid={`checklist-upload-${item.doc_type}`}
      >
        {busy === item.doc_type ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        <span className="ml-1">{item.uploaded ? 'Replace' : 'Upload'}</span>
      </Button>
    </div>
  </div>
);

export const OrderDocumentChecklist = ({ orderId, refreshKey = 0, onChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);
  const pendingType = useRef(null);

  const fetchChecklist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/order/${orderId}/checklist`, { headers: authHeaders() });
      setData(res.data);
    } catch {
      toast.error('Could not load document checklist');
    }
  }, [orderId]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist, refreshKey]);

  const startUpload = (docType) => {
    pendingType.current = docType;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const docType = pendingType.current;
    if (!file || !docType) return;
    setBusy(docType);
    try {
      await uploadDocument({ file, documentType: docType, orderId });
      toast.success(`${file.name} uploaded`);
      await fetchChecklist();
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = (doc) => downloadUploadedDocument(doc).catch(() => toast.error('Download failed'));

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;

  return (
    <div className="space-y-3" data-testid="order-document-checklist">
      <div>
        <div className="flex items-center justify-between">
          <p className="font-semibold text-charcoal">Required Documents</p>
          <span className="text-sm font-medium text-continental" data-testid="checklist-progress-text">
            {data.uploaded_count} of {data.total_count} uploaded
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Based on {data.hs_code ? `HS ${data.hs_code}` : 'the product'} and the route {data.origin_country || 'origin'} → {data.destination_country || 'destination'}
        </p>
        <Progress value={(data.uploaded_count / Math.max(1, data.total_count)) * 100} />
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} data-testid="checklist-file-input" />
      {data.items.map((item) => (
        <ChecklistItem key={item.doc_type} item={item} busy={busy} onUpload={startUpload} onDownload={handleDownload} />
      ))}
    </div>
  );
};

export default OrderDocumentChecklist;
